import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  LocalStorageBackend,
  RedirectRequestHandler,
  TokenRequest,
} from '@openid/appauth';
import { getFullJwt } from './getFullJwt';
import { getPartialJwt } from './getPartialJwt';
import { NoHashQueryStringUtils } from './noHashQueryStringUtils';
import ERROR_CODES from '../ErrorCodes';

export default class OIDCAuthManager {
  constructor(config) {
    this.config = config;
    this.notifier = new AuthorizationNotifier();
    this.tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    this.authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
    );
    // set notifier to deliver responses
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
  }

  async getAuthServiceConfiguration() {
    // Fetches auth service configuration, if it doesn't yet exist
    if (!this.authServiceConfiguration) {
      const response = await AuthorizationServiceConfiguration.fetchFromIssuer(
        this.config.authority,
        new FetchRequestor(),
      );

      this.authServiceConfiguration = response;
    }
  }

  async performStepUpAuthentication(locale, formName, clientNumber, certificate, logger) {
    const extras = {
      ui_locales: locale,
      claims: JSON.stringify({
        id_token: { acr: { essential: true, values: ['stepup_conditional'] }, ui_locales: { value: locale } },
      }),
    };

    // add language, formName, certificate, and clientNumber in the OIDC state
    const UUID = crypto.randomUUID();

    logger.info(`OIDCAuthManager: Step up authentication initiated. TransactionId: ${UUID}`);

    // Send logs before redirecting to another domain
    await logger.__sendLogsFromBuffer();

    const state = `gb_${btoa(
      `${UUID}&clientNumber=${clientNumber}&certificate=${certificate}&formName=${formName}&lang=${locale}`,
    )}`;

    // Create a request to step up at identity provider
    const authRequest = new AuthorizationRequest({
      client_id: this.config.client_id,
      redirect_uri: this.config.redirect_uri,
      scope: this.config.scope,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state,
      extras,
    });

    try {
      await this.getAuthServiceConfiguration();

      // Redirect to the identity provider login using the provided configuration
      this.authorizationHandler.performAuthorizationRequest(this.authServiceConfiguration, authRequest);
    } catch (err) {
      logger.error(`OIDCAuthManager: Error performing step up authentication. ERROR: ${err.message}`);
      throw new Error(ERROR_CODES.STEP_UP_INITIATION_FAILED);
    }
  }

  async stepUpCallback(responseHandler, logger) {
    this.notifier.setAuthorizationListener(this.createAuthorizationListener(responseHandler, logger));

    // Start the processing of identity provider response (callback)
    return this.authorizationHandler.completeAuthorizationRequestIfPossible();
  }

  createAuthorizationListener(responseHandler, logger) {
    // This listener is created to do the following:
    // - listen for authorization responses
    // - make id, refresh, and access token requests (using auth response)
    // - calls responseHandler callback function which handles the redirect after successful step up

    return async (request, response, error) => {
      let transactionId;

      if (response && !error) {
        try {
          // Get code verifier (PKCE) from authorization request to enable validation of auth code
          const codeVerifier = request?.internal?.code_verifier;
          // get language, formName, client number, and certificate from OIDC state
          const decodedState = atob(response.state.split('gb_')[1]);

          const stateParams = new URLSearchParams(decodedState);

          transactionId = decodedState.split('&')[0];

          const formName = stateParams.get('formName');
          const clientNumber = stateParams.get('clientNumber');
          const certificate = stateParams.get('certificate');
          const lang = stateParams.get('lang');

          logger.info(`OIDCAuthManager: Step up response received. TransactionId: ${transactionId}`);

          // Request for tokens: id token, access token, and refresh token - using the auth code
          const tokenResponse = await this.makeTokenRequest(response.code, codeVerifier);


          const partialJWT = await getPartialJwt(tokenResponse.idToken, transactionId);
          const fullJWT = await getFullJwt(partialJWT, clientNumber, certificate, transactionId);
          const jwt = fullJWT['gb-authorization'];
          const sessionCorrelationId = fullJWT['mli-session-correlation-id'];

          logger.info(`OIDCAuthManager: Step up authentication successful. TransactionId: ${transactionId}`);

          // Call response handler. This function updates redux state and redirects to return path.
          return responseHandler(jwt, sessionCorrelationId, formName, lang);
          // TODO: Update response handler for Step 3
        } catch (err) {
          logger.error(
            `OIDCAuthManager: Step up authentication failed. TransactionId: ${transactionId}. Error: ${err.message}`,
          );
          // Error making token request from Auth Service or BFF
          return responseHandler();
        }
      }

      logger.error(`OIDCAuthManager: Step up redirect error: ${error}`);
      // If there is an error value or there is no response
      return responseHandler();
    };
  }

  async makeTokenRequest(code, codeVerifier) {
    const extras = { code_verifier: codeVerifier };

    // Create a request for tokens (access token, id token, refresh token)
    const tokenRequest = new TokenRequest({
      client_id: this.config.client_id,
      redirect_uri: this.config.redirect_uri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      refresh_token: null,
      code,
      extras,
    });

    await this.getAuthServiceConfiguration();

    // Request for tokens (id token, access token, and refresh token) using the provided configuration
    const tokenResponse = await this.tokenHandler.performTokenRequest(this.authServiceConfiguration, tokenRequest);

    return tokenResponse;
  }
}
